import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { API, graphqlOperation } from "aws-amplify";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddTaskIcon from "@material-ui/icons/AssignmentTurnedIn";

import { Title, useNotify } from "react-admin";

import { useUser } from "../../../authenticatorApp/UserContext";
import { ForceTitle } from "../../../components/common/ForceTitle";
import {
  InboundIcon,
  OpenInNewIcon,
  OutboundIcon,
} from "../../../components/common/iconography";
import ShipsTogetherBadge from "../../../components/common/shipWith/ShipsTogetherBadge";
import { DELIVERIES_PATH, PICKUPS_PATH } from "../../carrierPaths";
import { carrierStatusChipLabel } from "../carrierStatusLabels";
import { isConditionalCheckFailed, logError } from "../../../utils/appUtils";
import {
  fetchShipWithMembers,
  isLinked,
  shipWithCacheKey,
} from "../../../utils/shipWith";
import { normalizeValue } from "../../../utils/utils";
import { searchOutboundShipments } from "../../../graphql/outbound/custom-queries";
import { searchInboundShipments } from "../../../graphql/inbound/custom-queries";
import { updateOutboundShipmentMini } from "../../../graphql/outbound/custom-mutations";
import { updateInboundShipmentMini } from "../../../graphql/inbound/custom-mutations";
import {
  CarrierType,
  InboundStatus,
  OutboundStatus,
} from "../../../utils/constants";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  intro: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardContent: {
    flexGrow: 1,
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  results: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  result: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  resultRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
  },
  resultLabel: {
    color: theme.palette.text.secondary,
  },
  claimButton: {
    marginTop: theme.spacing(1.5),
  },
  emptyMsg: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
}));

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const formatAddress = (addr) => {
  if (!addr) return null;
  const parts = [addr.city, addr.state].filter(Boolean);
  if (addr.name)
    return `${addr.name}${parts.length ? ` (${parts.join(", ")})` : ""}`;
  return parts.join(", ") || null;
};

const ResultRow = ({ label, value, classes }) =>
  value ? (
    <div className={classes.resultRow}>
      <Typography variant="body2" className={classes.resultLabel}>
        {label}
      </Typography>
      <Typography variant="body2">
        <strong>{value}</strong>
      </Typography>
    </div>
  ) : null;

// These run against the OpenSearch-backed search* queries, whose
// SearchableIDFilterInput offers `exists`. An unset or null carrierId is simply
// not indexed, so `exists: false` covers both; the blank variants catch rows that
// were saved with an empty string. This carrier's own id is a leg too, so a load
// it already claimed still answers a search instead of reading as "not found" —
// those rows come back as ones to open rather than ones to claim.
const searchableFilter = (carrierId) => [
  { carrierId: { exists: false } },
  { carrierId: { eq: "" } },
  { carrierId: { eq: " " } },
  ...(carrierId ? [{ carrierId: { eq: carrierId } }] : []),
];

// Belt-and-braces against a filter leg drifting: a yard-named carrier with no
// carrierId still counts as taken, and a blank carrierId counts as free — matching
// searchableFilter rather than plain truthiness, which would hide the " " rows the
// filter deliberately surfaces.
const isUnclaimed = (load) =>
  !load.carrierName?.trim() && !load.carrierId?.trim();

// Already this carrier's. Keyed on carrierId alone — the field the claim writes
// and the one the added filter leg matches — since a carrierName the yard typed by
// hand is not a claim.
const isClaimedByMe = (load, carrierId) =>
  Boolean(carrierId) && load.carrierId?.trim() === carrierId;

// A transfer is a yard-internal movement between two accounts. The yard stamps it
// carrierName: "TRANSFER" with carrierType TRUCK and PENDING; rows written before
// that marker moved off the legacy `carrier` scalar have no carrierName at all,
// and those satisfy every leg of the unclaimed filter — without this they read as
// open truck loads. Claiming one either way would put an outside carrier on the
// yard's own move and overwrite the marker the outbound export and the demurrage
// search read. Outbound only; InboundShipment has no transfer.
//
// Blank counts as not-a-transfer, matching the isEmpty(transfer) test the manager
// form uses to decide the same thing — so this can never hide a real load.
const notTransferFilter = [
  { transfer: { exists: false } },
  { transfer: { eq: "" } },
];

const isTransfer = (load) => !!load.transfer?.trim();

// Where one load stands for this carrier: free to take, already ours, or
// somebody else's — which includes a yard-typed carrierName with no id. The two
// tests are the ones the search filter already applies to its own hits, so a
// member read by id is judged exactly as a member the search returned.
const FREE = "free";
const MINE = "mine";
const TAKEN = "taken";

const ownershipOf = (load, carrierId) => {
  if (isClaimedByMe(load, carrierId)) return MINE;
  if (isUnclaimed(load)) return FREE;
  return TAKEN;
};

// A load may be claimed only while it still waits on the yard — the same two
// statuses the link lambda calls schedulable. Anything later (ARRIVED onward)
// is a truck already in motion under whoever is driving it.
const CLAIMABLE_STATUSES = [
  OutboundStatus.ADVICE_PENDING,
  OutboundStatus.PENDING,
];

// The claim mutation's condition is optimistic concurrency against the row as
// the search returned it, not a second guess at "unclaimed" in the DynamoDB
// Model*ConditionInput dialect — which has no `exists` to match searchableFilter
// with. A legacy blank carrierId is asserted as the blank it is; a missing one
// comes back null, and attribute_exists covers unset and nulled alike since the
// update resolver REMOVEs attributes whose input value is null.
//
// A load already ours is asserted as still ours instead, which is what lets a
// retry finish a truck whose first attempt stopped part way through. A load
// somebody else holds never reaches here — handleClaim refuses the whole truck
// before writing anything — and must not: asserting it against its own
// carrierId would pass and quietly overwrite their claim.
const claimCondition = (load, carrierId) =>
  ownershipOf(load, carrierId) === MINE
    ? { carrierId: { eq: carrierId } }
    : load.carrierId == null
      ? { carrierId: { attributeExists: false } }
      : { carrierId: { eq: load.carrierId } };

// A truck is this carrier's only when every load on it is. A group claimed part
// way through is not, so its card keeps offering Claim rather than Open — which
// is what makes the retry reachable at all. A member that could not be read
// leaves the truck unfinished by the same rule.
const isLoadMine = (load, carrierId) =>
  load.shipWithMembers
    ? load.shipWithMembers.every(
        (member) => !member?.unavailable && isClaimedByMe(member, carrierId),
      )
    : isClaimedByMe(load, carrierId);

// One truck, one card (AD-10). Loads linked as "ship with" ride on one truck
// and are claimed together, so members of one group collapse into a single
// result carrying every member as `shipWithMembers`. A search for one PO
// returns only the load that carries it, so the siblings are read by id; a
// member the search itself returned keeps its search projection (that is the
// row its claim condition is optimistic against), the rest use the narrow group
// read. A member that could not be read still counts on the card, and blocks
// the claim — resolveClaimGroup re-reads the truck at claim time and refuses
// one it cannot read in full. Singletons pass through untouched.
const collapseShipWithGroups = async (loads) => {
  const results = [];
  const groups = new Map();
  for (const load of loads) {
    if (!isLinked(load)) {
      results.push(load);
      continue;
    }
    const key = load.shipWithGroupId || shipWithCacheKey(load.shipWithIds);
    const group = groups.get(key);
    if (group) {
      group.hits.set(load.id, load);
    } else {
      const entry = { lead: load, hits: new Map([[load.id, load]]) };
      groups.set(key, entry);
      results.push(entry);
    }
  }
  return Promise.all(
    results.map(async (entry) => {
      if (!entry.hits) return entry;
      const fetched = await fetchShipWithMembers(entry.lead.shipWithIds);
      const members = fetched.map((m) => entry.hits.get(m.id) || m);
      return { ...entry.lead, shipWithMembers: members };
    }),
  );
};

// Distinct, non-blank values of one field across a group, in member order.
const distinctValues = (members, pick) =>
  Array.from(
    new Set(
      members.map((m) => (m?.unavailable ? null : pick(m))).filter(Boolean),
    ),
  );

// A claim navigates to the shipment's own edit route — the same route the list
// links to — and marks the navigation so the carrier edit views can serve the
// one-page schedule form here and the full dispatch form everywhere else. Writer
// and reader live together so the flag cannot drift.
const CLAIM_LANDING_STATE = { justClaimed: true };

export const isClaimLanding = (location) =>
  Boolean(location?.state?.justClaimed);

// The junction record carries the carrier's denormalized businessName — the same
// value the yard sees on its own carrier list — so a claim can stamp the shipment
// header without a second fetch.
const carrierNameOf = (appUser) =>
  appUser?.reloadYardCarrier?.businessName?.trim() || null;

const buildOutboundSearchFilter = (reloadYardId, carrierId, value) => {
  const normalizedValue = normalizeValue(value);
  return {
    reloadYardId: { eq: reloadYardId },
    carrierType: { eq: CarrierType.TRUCK },
    status: { eq: OutboundStatus.PENDING },
    and: [
      { or: searchableFilter(carrierId) },
      { or: notTransferFilter },
      {
        or: [
          { normalizedPickupNumber: { eq: `${normalizedValue}` } },
          { normalizedPoNumber: { eq: `${normalizedValue}` } },
          { normalizedConsigneePoNumber: { eq: `${normalizedValue}` } },
          { normalizedRailCarNumber: { eq: `${normalizedValue}` } },
        ],
      },
    ],
  };
};

const buildInboundSearchFilter = (reloadYardId, carrierId, value) => {
  const normalizedValue = normalizeValue(value);
  return {
    reloadYardId: { eq: reloadYardId },
    carrierType: { eq: CarrierType.TRUCK },
    status: { eq: InboundStatus.IN_TRANSIT },
    and: [
      { or: searchableFilter(carrierId) },
      {
        or: [
          { normalizedPoNumber: { eq: `${normalizedValue}` } },
          { normalizedRailCarNumber: { eq: `${normalizedValue}` } },
        ],
      },
    ],
  };
};

// One button, two jobs: a free load gets claimed and scheduled, a load this
// carrier already claimed just opens. Shared by both result rows so the pickup and
// delivery paths cannot drift apart.
const LoadActionButton = ({ classes, mine, claiming, onClaim, onOpen }) => (
  <Button
    className={classes.claimButton}
    variant="contained"
    color="primary"
    startIcon={
      mine ? (
        <OpenInNewIcon />
      ) : claiming ? (
        <CircularProgress size={16} color="inherit" />
      ) : (
        <AddTaskIcon />
      )
    }
    disabled={claiming}
    onClick={mine ? onOpen : onClaim}
    fullWidth
  >
    {mine ? "Open" : claiming ? "Claiming…" : "Claim and Schedule"}
  </Button>
);

const SearchCard = ({
  title,
  subheader,
  icon,
  placeholder,
  helperText,
  searchTerm,
  setSearchTerm,
  onSearch,
  loading,
  searched,
  results,
  emptyMsg,
  renderResult,
  classes,
}) => (
  <Card className={classes.card}>
    <CardHeader avatar={icon} title={title} subheader={subheader} />
    <Divider />
    <CardContent className={classes.cardContent}>
      <form onSubmit={onSearch}>
        <TextField
          id={title?.toLowerCase() + "-search"}
          className={classes.field}
          label="Search"
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          required
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          inputProps={{ "aria-label": "Search reference number" }}
          // The submit button lives in the adornment, so Enter still submits the
          // form the way it did when the button sat below the field.
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  type="submit"
                  edge="end"
                  color="primary"
                  aria-label="Find loads"
                  disabled={loading || !searchTerm.trim()}
                >
                  {loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SearchIcon />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          helperText={helperText}
        />
      </form>

      {searched && results.length === 0 && (
        <Typography className={classes.emptyMsg}>{emptyMsg}</Typography>
      )}

      {results.length > 0 && (
        <div className={classes.results}>{results.map(renderResult)}</div>
      )}
    </CardContent>
  </Card>
);

const PickupSearchCard = ({ appUser, classes }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const notify = useNotify();
  const history = useHistory();
  const carrierId = appUser?.carrierId;

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const value = searchTerm.trim();
    if (!value) return;
    setLoading(true);
    setSearched(false);
    setResults([]);
    try {
      const response = await API.graphql(
        graphqlOperation(searchOutboundShipments, {
          filter: buildOutboundSearchFilter(
            appUser?.reloadYardId,
            carrierId,
            value,
          ),
          limit: 25,
        }),
      );
      const items = response?.data?.searchOutboundShipments?.items || [];
      const available = items.filter(
        (i) =>
          (isUnclaimed(i) || isClaimedByMe(i, carrierId)) && !isTransfer(i),
      );
      setResults(await collapseShipWithGroups(available));
      setSearched(true);
    } catch (err) {
      logError("ClaimLoadPage:PickupSearch", err, notify);
    } finally {
      setLoading(false);
    }
  };

  // The card is only as fresh as the last search, so the truck is read again at
  // the moment of the claim. Two things can have moved while it sat on screen:
  // its membership, if a manager linked or unlinked a load, and its ownership,
  // if another carrier claimed part of it. Both decide what may be written, so
  // neither is taken from the card.
  //
  // Returns the members to stamp, in id order, or null when the claim must not
  // go ahead — in which case the carrier has been told why and the search has
  // run again, so the card they look at next is the truth.
  const resolveClaimGroup = async (load) => {
    const expected = shipWithCacheKey(load.shipWithIds);
    const members = await fetchShipWithMembers(load.shipWithIds);
    const refuse = async (message) => {
      notify(message, "warning");
      await handleSearch();
      return null;
    };

    // An unread member is one whose owner and group are both unknown, and a
    // truck is claimed whole or not at all — so nothing is written.
    if (members.some((member) => member?.unavailable))
      return refuse("This truck could not be read in full — try again");

    // Every member names the same group, so any one of them disagreeing with
    // the card means the truck itself changed under it.
    if (members.some((m) => shipWithCacheKey(m.shipWithIds) !== expected))
      return refuse(
        "The loads on this truck just changed — check and claim again",
      );

    if (members.some((m) => ownershipOf(m, carrierId) === TAKEN))
      return refuse("Part of this truck is claimed by another carrier");

    // The group read carries the gate and status fields for exactly this: a
    // member checked in at the gate is a truck already on a visit (the lock
    // the link lambda refuses on too), and one past pending is already being
    // worked at the yard. Stamping a carrier onto either would put this
    // carrier on a truck some other driver is making. The search only returns
    // PENDING loads, so only a sibling can be in either state.
    if (members.some((m) => m.gateCheckInId))
      return refuse("This truck is already checked in at the gate");
    if (members.some((m) => !CLAIMABLE_STATUSES.includes(m.status)))
      return refuse("This truck is no longer pending and cannot be claimed");

    return [...members].sort((a, b) => (a.id < b.id ? -1 : 1));
  };

  // Claiming stamps the carrier onto the shipment header: carrierId links the
  // record and carrierName is what the manager portal displays. The legacy
  // `carrier` scalar gets the same value because two yard-facing readers still go
  // to it and not to carrierName — the outbound export's Carrier column and the
  // demurrage report's carrier search. Nothing else in the app writes it outside
  // the transfer paths, so the two fields otherwise drift.
  //
  // Everything else stays the yard's, and the carrier's own edits happen on the
  // dispatch form we land on next.
  const handleClaim = async (load) => {
    setClaimingId(load.id);
    const carrierName = carrierNameOf(appUser);
    const stamp = (member) =>
      API.graphql(
        graphqlOperation(updateOutboundShipmentMini, {
          input: {
            id: member.id,
            carrierId: appUser.carrierId,
            carrierName,
            carrier: carrierName,
          },
          condition: claimCondition(member, carrierId),
        }),
      );
    try {
      if (load.shipWithMembers) {
        // A linked group is one truck, so every member is stamped, each under
        // its own condition (AD-10) — one at a time in id order, stopping at
        // the first failure. The fixed order keeps the truck whole: two
        // carriers claiming at once both start on the same member, and the
        // one that loses it never touches the rest. A failure past the first
        // member (a dropped request, a member someone held before the link)
        // leaves the ones before it ours, so the search runs again and shows
        // what is actually left; claiming again converges, since a member
        // already ours is claimed under "still mine". Anything else is a fault.
        const members = await resolveClaimGroup(load);
        if (!members) return;
        for (const [i, member] of members.entries()) {
          try {
            await stamp(member);
          } catch (err) {
            const lost = isConditionalCheckFailed(err);
            if (lost) {
              notify(
                i === 0
                  ? "This truck was just claimed by another carrier"
                  : "Part of this truck was just claimed by another carrier",
                "warning",
              );
            } else {
              logError("ClaimLoadPage:claimOutbound", err, notify);
            }
            if (lost || i > 0) await handleSearch();
            return;
          }
        }
      } else {
        await stamp(load);
      }
      notify("Load claimed", "success");
      history.push({
        pathname: `${PICKUPS_PATH}/${load.id}`,
        state: CLAIM_LANDING_STATE,
      });
    } catch (err) {
      if (isConditionalCheckFailed(err)) {
        notify("This load was just claimed by another carrier", "warning");
        setResults((prev) => prev.filter((r) => r.id !== load.id));
      } else {
        logError("ClaimLoadPage:claimOutbound", err, notify);
      }
    } finally {
      setClaimingId(null);
    }
  };

  // A load this carrier already owns has nothing to claim, so it goes straight to
  // the shipment's edit route without CLAIM_LANDING_STATE — the same full dispatch
  // form the list links to, rather than the one-page schedule form a fresh claim
  // lands on.
  const handleOpen = (load) => history.push(`${PICKUPS_PATH}/${load.id}`);

  return (
    <SearchCard
      title="Outbound"
      subheader="Pickup Load"
      icon={<OutboundIcon color="primary" />}
      helperText="Search by any reference number printed on the load tender."
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      onSearch={handleSearch}
      loading={loading}
      searched={searched}
      results={results}
      emptyMsg="No pickup loads match that reference at this yard."
      renderResult={(load) => (
        <OutboundResult
          key={load.id}
          load={load}
          classes={classes}
          claiming={claimingId === load.id}
          mine={isLoadMine(load, carrierId)}
          onClaim={() => handleClaim(load)}
          onOpen={() => handleOpen(load)}
        />
      )}
      classes={classes}
    />
  );
};

// A linked group renders as one card for the truck: how many loads it carries,
// every PO and pickup number the carrier is taking on, and each consignee on
// the route. The account and the appointment are the group's (members share
// one Timeslot), so those rows read from the lead as for a single load.
const OutboundResult = ({ load, classes, claiming, mine, onClaim, onOpen }) => {
  const members = load.shipWithMembers;
  return (
    <div className={classes.result}>
      <Box display="flex" alignItems="center" mb={1}>
        {members ? (
          <Box display="flex" alignItems="center" flexGrow={1}>
            <Typography variant="body2">
              {members.length} loads on one truck
            </Typography>
            <Box ml={1} display="inline-flex">
              <ShipsTogetherBadge record={load} withTooltip={false} />
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" style={{ flexGrow: 1 }}>
            Pickup #: {load.pickupNumber || "—"}
          </Typography>
        )}
        {load.status && <Chip size="small" label={load.status} />}
      </Box>
      {members && (
        <ResultRow
          label="PO #s"
          value={distinctValues(members, (m) => m.poNumber).join(", ")}
          classes={classes}
        />
      )}
      {members && (
        <ResultRow
          label="Pickup #s"
          value={distinctValues(members, (m) => m.pickupNumber).join(", ")}
          classes={classes}
        />
      )}
      <ResultRow
        label="Account"
        value={load.accountName || load.account?.displayName}
        classes={classes}
      />
      <ResultRow
        label={members ? "Consignees" : "Consignee"}
        value={
          members
            ? distinctValues(members, (m) => formatAddress(m.shipTo)).join(
                " · ",
              )
            : formatAddress(load.shipTo)
        }
        classes={classes}
      />
      <ResultRow
        label="Scheduled"
        value={formatDate(load.scheduledDateTime)}
        classes={classes}
      />
      <LoadActionButton
        classes={classes}
        mine={mine}
        claiming={claiming}
        onClaim={onClaim}
        onOpen={onOpen}
      />
    </div>
  );
};

const DeliverySearchCard = ({ appUser, classes }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [claimingId, setClaimingId] = useState(null);
  const notify = useNotify();
  const history = useHistory();
  const carrierId = appUser?.carrierId;

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    const value = searchTerm.trim();
    if (!value) return;
    setLoading(true);
    setSearched(false);
    setResults([]);
    try {
      const response = await API.graphql(
        graphqlOperation(searchInboundShipments, {
          filter: buildInboundSearchFilter(
            appUser?.reloadYardId,
            carrierId,
            value,
          ),
          limit: 25,
        }),
      );
      const items = response?.data?.searchInboundShipments?.items || [];
      const available = items.filter(
        (i) => isUnclaimed(i) || isClaimedByMe(i, carrierId),
      );
      setResults(available);
      setSearched(true);
    } catch (err) {
      logError("ClaimLoadPage:DeliverySearch", err, notify);
    } finally {
      setLoading(false);
    }
  };

  // As with the pickup claim, but InboundShipment has no legacy `carrier` scalar
  // to mirror — there the name is `carrierName` alone (`carrier` is the Carrier
  // connection).
  const handleClaim = async (load) => {
    setClaimingId(load.id);
    const carrierName = carrierNameOf(appUser);
    try {
      await API.graphql(
        graphqlOperation(updateInboundShipmentMini, {
          input: {
            id: load.id,
            carrierId: appUser.carrierId,
            carrierName,
          },
          condition: claimCondition(load, carrierId),
        }),
      );
      notify("Load claimed", "success");
      history.push({
        pathname: `${DELIVERIES_PATH}/${load.id}`,
        state: CLAIM_LANDING_STATE,
      });
    } catch (err) {
      if (isConditionalCheckFailed(err)) {
        notify("This load was just claimed by another carrier", "warning");
        setResults((prev) => prev.filter((r) => r.id !== load.id));
      } else {
        logError("ClaimLoadPage:claimInbound", err, notify);
      }
    } finally {
      setClaimingId(null);
    }
  };

  // As with the pickup card: already ours means no claim and no
  // CLAIM_LANDING_STATE, so the route serves the full dispatch form.
  const handleOpen = (load) => history.push(`${DELIVERIES_PATH}/${load.id}`);

  return (
    <SearchCard
      title="Inbound"
      subheader="Delivery Load"
      icon={<InboundIcon color="primary" />}
      helperText="Search by any reference number printed on the load tender."
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      onSearch={handleSearch}
      loading={loading}
      searched={searched}
      results={results}
      emptyMsg="No delivery loads match that reference at this yard."
      renderResult={(load) => (
        <InboundResult
          key={load.id}
          load={load}
          classes={classes}
          claiming={claimingId === load.id}
          mine={isClaimedByMe(load, carrierId)}
          onClaim={() => handleClaim(load)}
          onOpen={() => handleOpen(load)}
        />
      )}
      classes={classes}
    />
  );
};

const InboundResult = ({ load, classes, claiming, mine, onClaim, onOpen }) => (
  <div className={classes.result}>
    <Box display="flex" alignItems="center" mb={1}>
      <Typography variant="body2" style={{ flexGrow: 1 }}>
        PO #: {load.poNumber || "—"}
      </Typography>
      {load.status && (
        <Chip size="small" label={carrierStatusChipLabel(load.status)} />
      )}
    </Box>
    <ResultRow
      label="Account"
      value={load.accountName || load.account?.displayName}
      classes={classes}
    />
    <ResultRow label="Origin" value={load.shipFrom} classes={classes} />
    <ResultRow
      label="Scheduled"
      value={formatDate(load.scheduledDateTime)}
      classes={classes}
    />
    <LoadActionButton
      classes={classes}
      mine={mine}
      claiming={claiming}
      onClaim={onClaim}
      onOpen={onOpen}
    />
  </div>
);

/**
 * The claim form on its own, so it can be dropped into the carrier dashboard as
 * well as served as a standalone page.
 */
export const ClaimLoadPanel = ({ className }) => {
  const classes = useStyles();
  const { appUser } = useUser();

  return (
    <div className={className}>
      <Typography variant="h5" gutterBottom>
        Schedule a Load
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <DeliverySearchCard appUser={appUser} classes={classes} />
        </Grid>
        <Grid item xs={12} md={6}>
          <PickupSearchCard appUser={appUser} classes={classes} />
        </Grid>
      </Grid>
    </div>
  );
};

export const ClaimLoadPage = () => {
  const classes = useStyles();

  return (
    <>
      <Title title={<ForceTitle title="Claim a Load" />} />
      <ClaimLoadPanel className={classes.root} />
    </>
  );
};

export default ClaimLoadPage;
